import { AnyAction } from 'redux';
import { call, put } from 'redux-saga/effects';
import {
  BasisStreckenState,
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  ShopTarifierungRequestEvents,
  TarifiereAngebotForShopAction,
} from '@ps20-frontend/shop-integration-redux';
import { App, BeziehungZumVn, getTarifbereich } from '@ps20-frontend/frontend-models';
import { Feature, SpartenType, TaasRequestModel, TaasResponseModel } from '@ps20/bff-models';
import { CallEffect } from '@redux-saga/core/effects'; // NOSONAR
import { getSuccessEventForShop } from '../requestEventMapper';
import {
  buildTaasRequestModelForShop,
  createHashedTaasData,
  getTaasVertragsoptionen,
  TaasData,
} from '../taasRequestHelper';
import { BasisTarifierer } from './basisTarifierer';
import { ShopTarifierungsErgebnisHandler } from './shopTarifierungsErgebnisHandler';

export interface ShopTarifiererConfig<T extends BasisStreckenState, U extends TaasData> {
  appId: App;
  taasDataBuilder: (state: T) => U;
  hashFunc?: (spartenDaten: U) => string;
  resultHandler: ShopTarifierungsErgebnisHandler<T, U>;
  shouldCancelRequestFunc?: (state: T, action: AnyAction) => boolean;
}

export class ShopTarifierer<
  T extends BasisStreckenState,
  U extends TaasData,
> extends BasisTarifierer<T, U> {
  private readonly createHashedData: (taasData: U) => string = createHashedTaasData;

  constructor(private readonly shopTarifiererConfig: ShopTarifiererConfig<T, U>) {
    super(
      shopTarifiererConfig.appId,
      shopTarifiererConfig.taasDataBuilder,
      shopTarifiererConfig.shouldCancelRequestFunc,
    );
    this.createHashedData = shopTarifiererConfig.hashFunc || this.createHashedData;
  }

  *handleSuccess(
    successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<U>>,
  ): Generator<CallEffect> {
    yield call(
      this.shopTarifiererConfig.resultHandler.verarbeiteTarifierungsErgebnis,
      successAction,
    );
  }

  *handleError(): Generator<CallEffect> {
    yield call(GlobalPut.getInstance().put, new GlobalActions().errorPageRequested());
  }

  protected *buildTaasRequestModel(state: T): Generator<unknown, TaasRequestModel<U>, unknown> {
    const taasData = this.buildTaasData(state);
    const taasRequestHash = this.createHashedData(taasData);
    yield put(new GlobalActions().newHashedTaasRequest(this.appId, taasRequestHash));

    return buildTaasRequestModelForShop(
      taasData,
      taasRequestHash,
      getTaasVertragsoptionen(state.vertragsoptionen, taasData.spartenType, {
        aaWorldTkvFT: state.transientState.featureToggles[Feature.AA_WORLD_TKV],
        aktion10FT: state.transientState.featureToggles[Feature.AKTION_10],
        tier10FT: state.transientState.featureToggles[Feature.TIER_10],
      }),
      state.taasInfo,
      getTarifbereich(state.versicherungsnehmer, this.getVpBeziehungZumVn(state as T & { beziehungVpZumVn?: BeziehungZumVn })),
      state.vnWechselAngaben
    );
  }

  private getVpBeziehungZumVn(state: T & { beziehungVpZumVn?: BeziehungZumVn }): BeziehungZumVn {
    return this.isUnfall(state) ? state.beziehungVpZumVn : undefined;
  }

  private isUnfall(state: T): boolean {
    return state.produkt.spartenType === SpartenType.UNFALL;
  }

  protected getSuccessEvent(): string {
    return getSuccessEventForShop(this.appId);
  }

  protected getErrorEvent(): string {
    return ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_FAILED;
  }

  protected *checkTaasCallPreconditions(
    state: T,
    action: TarifiereAngebotForShopAction,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    if (action.payload.appId !== this.appId || state.angebotsId !== action.payload.angebotsId) {
      return false;
    }
    return yield* super.checkTaasCallPreconditions(state, action);
  }
}
